import {useCallback, useEffect, useReducer, useRef} from 'react';

import debounce from '@tinkoff/utils/function/debounce';
import {useAppDispatch} from 'stores/redux_store';
import {forceLogoutIfNecessaryThunkAxios} from 'mattermost-redux/actions/helpers';
import {CanceledError} from 'mattermost-redux/api/errors';
import {useEventCallback} from 'usehooks-ts';

import {
    LOADER_VIEW_TIMEOUT,
    RETRY_VIEW_TIMEOUT,
    SEARCH_DEBOUNCE_TIMEOUT,
} from './consts';
import type {Mention} from './types';

export enum MentionView {
    EMPTY_VIEW = 'EMPTY_VIEW',
    SUCCEED_VIEW = 'SUCCEED_VIEW',
    LOADER_VIEW = 'LOADER_VIEW',
    RETRY_VIEW = 'RETRY_VIEW',
}

type UseState<M extends Mention> = {
    mentions: M[];
    loading: boolean;
    view: MentionView;
};

enum UseMentionActionType {
    REQUEST_STARTED = 'REQUEST_STARTED',
    MENTIONS_RECEIVED = 'MENTIONS_RECEIVED',
    SET_RETRY_VIEW = 'SET_RETRY_VIEW',
    SET_LOADER_VIEW = 'SET_LOADER_VIEW',
    RESET = 'RESET',
}

type UseMentionAction<M extends Mention> = {
    type: UseMentionActionType.REQUEST_STARTED;
    payload: boolean;
} | {
    type: UseMentionActionType.SET_LOADER_VIEW;
} | {
    type: UseMentionActionType.MENTIONS_RECEIVED;
    payload: M[];
} | {
    type: UseMentionActionType.SET_RETRY_VIEW;
    payload: boolean;
} | {
    type: UseMentionActionType.RESET;
};

function useMentionReducer<M extends Mention>(state: UseState<M>, action: UseMentionAction<M>): UseState<M> {
    switch (action.type) {
    case UseMentionActionType.REQUEST_STARTED:
        if (state.view !== MentionView.RETRY_VIEW && !action.payload) {
            return {...state, view: MentionView.EMPTY_VIEW, loading: true};
        }
        return state;
    case UseMentionActionType.MENTIONS_RECEIVED:
        return {view: MentionView.SUCCEED_VIEW, mentions: action.payload, loading: false};
    case UseMentionActionType.SET_RETRY_VIEW:
        return {...state, view: MentionView.RETRY_VIEW, loading: action.payload};
    case UseMentionActionType.SET_LOADER_VIEW:
        return {...state, view: MentionView.LOADER_VIEW, loading: true};
    case UseMentionActionType.RESET:
        return {...initialState};
    default:
        throw new Error();
    }
}

const initialState = {
    mentions: [],
    loading: false,
    view: MentionView.EMPTY_VIEW,
};

export function useGetMentions<M extends Mention>(
    query: string,
    getMentionsRequest: (query: string) => Promise<M[]>,
    ignoreEmptyQuery = true,
) {
    const reduxDispatch = useAppDispatch();
    const [state, dispatch] = useReducer<typeof useMentionReducer<M>>(useMentionReducer, initialState);
    const loaderStartDelay = useRef<ReturnType<typeof setTimeout>>();
    const receivedResultsDelay = useRef<ReturnType<typeof setTimeout>>();

    const getMentions = useCallback(
        async (query: string, isRetrying: boolean = false) => {
            dispatch({type: UseMentionActionType.REQUEST_STARTED, payload: isRetrying});

            const startedAt = Date.now();

            clearTimeout(loaderStartDelay.current);
            clearTimeout(receivedResultsDelay.current);

            loaderStartDelay.current = setTimeout(() => {
                if (isRetrying) {
                    dispatch({type: UseMentionActionType.SET_RETRY_VIEW, payload: true});
                    return;
                }
                dispatch({type: UseMentionActionType.SET_LOADER_VIEW});
            }, isRetrying ? RETRY_VIEW_TIMEOUT.delay : LOADER_VIEW_TIMEOUT.delay);

            return getMentionsRequest(query)
                .then((mentions) => {
                    const finishedAt = Date.now();
                    const timeout = isRetrying ? RETRY_VIEW_TIMEOUT : LOADER_VIEW_TIMEOUT;

                    const duration = finishedAt - startedAt;
                    const threshold = timeout.delay + timeout.period;

                    if (duration < timeout.delay || duration > threshold) {
                        clearTimeout(loaderStartDelay.current);
                        dispatch({type: UseMentionActionType.MENTIONS_RECEIVED, payload: mentions});
                        return;
                    }

                    receivedResultsDelay.current = setTimeout(() => {
                        dispatch({type: UseMentionActionType.MENTIONS_RECEIVED, payload: mentions});
                    }, threshold - duration);
                })
                .catch((error) => {
                    if (error instanceof CanceledError) {
                        return;
                    }

                    dispatch({type: UseMentionActionType.SET_RETRY_VIEW, payload: false});

                    reduxDispatch(forceLogoutIfNecessaryThunkAxios(error));
                })
                .finally(() => {
                    clearTimeout(loaderStartDelay.current);
                    loaderStartDelay.current = undefined;
                });
        },
        [getMentionsRequest, reduxDispatch],
    );
    const reset = useCallback(() => {
        dispatch({type: UseMentionActionType.RESET});
    }, []);

    // comment: мемоизируем функцию завернутую в дебаунс
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetMentions = useCallback(debounce(SEARCH_DEBOUNCE_TIMEOUT, getMentions), [getMentions]);

    const retry = useEventCallback(() =>
        debouncedGetMentions(query, true),
    );

    const request = useEventCallback(() =>
        debouncedGetMentions(query, false),
    );

    useEffect(() => {
        if (query || !ignoreEmptyQuery) {
            debouncedGetMentions(query);
            return;
        }

        dispatch({type: UseMentionActionType.MENTIONS_RECEIVED, payload: []});
    }, [query, debouncedGetMentions, ignoreEmptyQuery]);

    return {
        ...state,
        retry,
        request,
        reset,
    };
}
